// src/components/teacher/TeacherAttendance.js
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import { CalendarIcon, SaveIcon } from "../shared/icons/Icons";
import { USE_DUMMY_DATA, DUMMY_COURSES, DUMMY_TIMETABLE, studentsByCourseId } from "./dummyData";

const STATUS_OPTIONS = ["Present", "Absent", "Late", "Leave"];

function TeacherAttendance({ courses = [], loading: parentLoading }) {
  const location = useLocation();
  const navigate = useNavigate();
  const sourceCourses = USE_DUMMY_DATA
    ? DUMMY_COURSES
    : courses.length
      ? courses
      : [];

  // Attendance is taken per CLASS (a class row already encodes its section),
  // so collapse the assigned courses down to their distinct classes. The
  // option value is the real class_id, which is what /api/attendance/mark
  // and /api/teachers/me/students both expect.
  const classKey = (c) => (USE_DUMMY_DATA ? c.id : c.class_id);
  const classList = Array.from(
    new Map(
      sourceCourses
        .filter((c) => classKey(c) != null)
        .map((c) => [
          classKey(c),
          {
            class_id: classKey(c),
            class_name: c.class_name,
            section: c.class_section || c.section,
          },
        ]),
    ).values(),
  );

  const resolveInitialClass = () => {
    if (location.state?.classId != null) return String(location.state.classId);
    if (location.state?.courseId != null) {
      const match = sourceCourses.find(
        (c) => (USE_DUMMY_DATA ? c.id : c.course_id) === location.state.courseId,
      );
      if (match) return String(classKey(match));
    }
    return "";
  };

  const [selectedClass, setSelectedClass] = useState(resolveInitialClass);
  const [selectedCourse, setSelectedCourse] = useState(
    location.state?.courseId != null ? String(location.state.courseId) : "",
  );
  const [selectedPeriod, setSelectedPeriod] = useState(
    location.state?.periodNumber != null ? String(location.state.periodNumber) : "",
  );
  const timetableEntryId = location.state?.timetableEntryId || null;
  const [attendanceDate, setAttendanceDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [summary, setSummary] = useState({
    present: 0,
    absent: 0,
    late: 0,
    leave: 0,
  });
  const [timetable, setTimetable] = useState(USE_DUMMY_DATA ? DUMMY_TIMETABLE : []);
  const [timetableLoading, setTimetableLoading] = useState(!USE_DUMMY_DATA);

  useEffect(() => {
    if (USE_DUMMY_DATA) {
      setTimetable(DUMMY_TIMETABLE);
      setTimetableLoading(false);
      return undefined;
    }
    let alive = true;
    (async () => {
      setTimetableLoading(true);
      try {
        const params = selectedClass ? `?class_id=${encodeURIComponent(selectedClass)}` : "";
        const response = await fetch(`${API_BASE_URL}/api/teachers/me/timetable${params}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error("Failed to fetch timetable");
        const data = await response.json();
        if (alive) setTimetable(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (alive) setTimetable([]);
      } finally {
        if (alive) setTimetableLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedClass]);

  const selectedWeekday = useMemo(() => {
    if (!attendanceDate) return "";
    const parsed = new Date(`${attendanceDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("en-US", { weekday: "long" });
  }, [attendanceDate]);

  const periodOptions = useMemo(() => {
    let rows = Array.isArray(timetable) ? timetable : [];
    if (selectedClass) {
      rows = rows.filter((entry) => String(entry.class_id) === String(selectedClass));
    }
    if (selectedCourse) {
      rows = rows.filter((entry) => String(entry.course_id) === String(selectedCourse));
    }
    if (selectedWeekday) {
      rows = rows.filter((entry) => String(entry.day_of_week) === selectedWeekday);
    }
    const unique = new Map();
    rows.forEach((entry) => {
      const period = Number(entry.period_number);
      if (!period || unique.has(period)) return;
      unique.set(period, entry);
    });
    return Array.from(unique.values()).sort(
      (a, b) => Number(a.period_number) - Number(b.period_number),
    );
  }, [timetable, selectedClass, selectedCourse, selectedWeekday]);

  useEffect(() => {
    const initial = resolveInitialClass();
    if (initial) {
      setSelectedClass(initial);
    }
    if (location.state?.courseId != null) {
      setSelectedCourse(String(location.state.courseId));
    }
    if (location.state?.periodNumber != null) {
      setSelectedPeriod(String(location.state.periodNumber));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filteredCourses = sourceCourses.filter(
    (c) => String(classKey(c)) === String(selectedClass),
  );

  useEffect(() => {
    if (!selectedClass) {
      setSelectedCourse("");
      setSelectedPeriod("");
      return;
    }
    if (
      selectedCourse &&
      !filteredCourses.some((c) => String(USE_DUMMY_DATA ? c.id : c.course_id) === String(selectedCourse))
    ) {
      setSelectedCourse("");
    }
  }, [filteredCourses, selectedClass, selectedCourse]);

  useEffect(() => {
    if (!selectedPeriod || timetableLoading) return;
    const stillAssigned = periodOptions.some(
      (entry) => String(entry.period_number) === String(selectedPeriod),
    );
    if (!stillAssigned) setSelectedPeriod("");
  }, [periodOptions, selectedPeriod, timetableLoading]);

  const updateSummary = (attendanceData) => {
    const values = Object.values(attendanceData);
    setSummary({
      present: values.filter((v) => v === "Present").length,
      absent: values.filter((v) => v === "Absent").length,
      late: values.filter((v) => v === "Late").length,
      leave: values.filter((v) => v === "Leave").length,
    });
  };

  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedClass) {
        setStudents([]);
        setAttendance({});
        updateSummary({});
        return;
      }

      setLoading(true);
      setError(null);

      try {
        if (USE_DUMMY_DATA) {
          const list = studentsByCourseId(selectedClass).filter(
            (s) => s.student_status === "Active",
          );
          setStudents(list);
          const initial = {};
          list.forEach((s) => {
            initial[s.id] = "Present";
          });
          setAttendance(initial);
          updateSummary(initial);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/teachers/me/students?class_id=${selectedClass}`,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch students");
        }

        const data = await response.json();
        const active = (Array.isArray(data) ? data : []).filter(
          (s) => s.student_status === "Active",
        );
        setStudents(active);
        const initial = {};
        active.forEach((s) => {
          initial[s.id] = "Present";
        });

        if (selectedCourse && selectedPeriod) {
          const params = new URLSearchParams({
            class_id: String(selectedClass),
            course_id: String(selectedCourse),
            attendance_date: attendanceDate,
            period_number: String(selectedPeriod),
          });
          const periodResponse = await fetch(`${API_BASE_URL}/api/attendance/period?${params}`, {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          });
          if (periodResponse.ok) {
            const saved = await periodResponse.json();
            (Array.isArray(saved) ? saved : []).forEach((row) => {
              initial[row.student_id] = row.status || "Present";
            });
          }
        }

        setAttendance(initial);
        updateSummary(initial);
      } catch (err) {
        console.error(err);
        setError("Could not load students for this class.");
      } finally {
        setLoading(false);
      }
    };

    loadStudents();
  }, [selectedClass, selectedCourse, selectedPeriod, attendanceDate]);

  const handleAttendanceChange = (studentId, status) => {
    const next = { ...attendance, [studentId]: status };
    setAttendance(next);
    updateSummary(next);
  };

  const handleSaveAttendance = async () => {
    if (!selectedClass || !selectedCourse || !selectedPeriod || !attendanceDate) {
      setError("Please select class, subject, period, and date.");
      return;
    }
    if (students.length === 0) {
      setError("No students to mark attendance for.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    if (USE_DUMMY_DATA) {
      setTimeout(() => {
        setSuccess(true);
        setSaving(false);
        setTimeout(() => setSuccess(false), 2500);
      }, 500);
      return;
    }

    try {
      const entries = students.map((student) => ({
        student_id: student.id,
        status: attendance[student.id] || "Present",
        remarks: "",
      }));

      const response = await fetch(`${API_BASE_URL}/api/attendance/period-mark`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          class_id: parseInt(selectedClass, 10),
          course_id: parseInt(selectedCourse, 10),
          period_number: parseInt(selectedPeriod, 10),
          date: attendanceDate,
          timetable_entry_id: timetableEntryId,
          entries,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to save attendance");
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to save attendance");
    } finally {
      setSaving(false);
    }
  };

  if (parentLoading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading attendance...</p>
      </div>
    );
  }

  const selectedClassMeta = classList.find(
    (c) => String(c.class_id) === String(selectedClass),
  );

  return (
    <div>
      <div className="tp-page-header">
        <div>
          <h2>
            <CalendarIcon />
            Take Attendance
          </h2>
          <p>
            {location.state?.courseName
              ? `Opened from timetable: ${location.state.courseName}`
              : "Mark subject-wise attendance for your assigned periods"}
          </p>
        </div>
        <button
          type="button"
          className="tp-btn tp-btn--ghost"
          onClick={() => navigate("/teacher/timetable")}
        >
          Back to Timetable
        </button>
      </div>

      {success ? (
        <div className="tp-toast">Attendance saved successfully.</div>
      ) : null}
      {error ? <div className="tp-toast tp-toast--error">{error}</div> : null}

      <div className="tp-filters">
        <select
          className="tp-select"
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
        >
          <option value="">Select class...</option>
          {classList.map((cls) => (
            <option key={cls.class_id} value={cls.class_id}>
              {cls.class_name}
              {cls.section ? ` — ${cls.section}` : ""}
            </option>
          ))}
        </select>
        <select
          className="tp-select"
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
          disabled={!selectedClass}
        >
          <option value="">Select subject...</option>
          {filteredCourses.map((course) => {
            const courseId = USE_DUMMY_DATA ? course.id : course.course_id;
            return (
              <option key={`${course.class_id || selectedClass}-${courseId}`} value={courseId}>
                {course.course_name || `Course ${courseId}`}
              </option>
            );
          })}
        </select>
        <select
          className="tp-select"
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          disabled={!selectedClass || timetableLoading}
        >
          <option value="">
            {timetableLoading
              ? "Loading periods..."
              : periodOptions.length
                ? "Select period..."
                : selectedCourse
                  ? "No assigned period for this day"
                  : "Select subject first..."}
          </option>
          {periodOptions.map((entry) => {
            const timeLabel =
              entry.start_time && entry.end_time
                ? ` (${String(entry.start_time).slice(0, 5)} – ${String(entry.end_time).slice(0, 5)})`
                : "";
            return (
              <option key={`${entry.id || entry.period_number}`} value={entry.period_number}>
                Period {entry.period_number}{timeLabel}
              </option>
            );
          })}
        </select>
        <input
          className="tp-input"
          type="date"
          value={attendanceDate}
          onChange={(e) => setAttendanceDate(e.target.value)}
        />
        <button
          type="button"
          className="tp-btn"
          onClick={handleSaveAttendance}
          disabled={
            saving ||
            !selectedClass ||
            !selectedCourse ||
            !selectedPeriod ||
            students.length === 0
          }
        >
          <SaveIcon /> {saving ? "Saving..." : "Save Attendance"}
        </button>
      </div>

      {selectedClassMeta ? (
        <div className="tp-header-badges" style={{ marginBottom: "1rem" }}>
          <span className="tp-badge tp-badge--today">
            {selectedClassMeta.class_name}
          </span>
          {selectedClassMeta.section ? (
            <span className="tp-badge">{selectedClassMeta.section}</span>
          ) : null}
          {selectedCourse ? (
            <span className="tp-badge tp-badge--today">
              {filteredCourses.find(
                (c) => String(USE_DUMMY_DATA ? c.id : c.course_id) === String(selectedCourse),
              )?.course_name || "Subject"}
            </span>
          ) : null}
          {selectedPeriod ? <span className="tp-badge">Period {selectedPeriod}</span> : null}
        </div>
      ) : null}

      {selectedClass ? (
        <>
          <div className="tp-att-summary">
            <div className="tp-att-pill">
              <strong style={{ color: "#16a34a" }}>{summary.present}</strong>
              <span>Present</span>
            </div>
            <div className="tp-att-pill">
              <strong style={{ color: "#dc2626" }}>{summary.absent}</strong>
              <span>Absent</span>
            </div>
            <div className="tp-att-pill">
              <strong style={{ color: "#d97706" }}>{summary.late}</strong>
              <span>Late</span>
            </div>
            <div className="tp-att-pill">
              <strong style={{ color: "#7c3aed" }}>{summary.leave}</strong>
              <span>Leave</span>
            </div>
          </div>

          {loading ? (
            <div className="tp-loading">
              <div className="tp-spinner" />
              <p>Loading students...</p>
            </div>
          ) : students.length > 0 ? (
            <div className="tp-table-wrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Father</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <span className="tp-mono">{student.student_id}</span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{student.name}</td>
                      <td>{student.father_name || "N/A"}</td>
                      <td>
                        <div className="tp-att-actions">
                          {STATUS_OPTIONS.map((status) => (
                            <button
                              key={status}
                              type="button"
                              className={`tp-att-mark${
                                attendance[student.id] === status
                                  ? " is-active"
                                  : ""
                              }`}
                              data-status={status}
                              onClick={() =>
                                handleAttendanceChange(student.id, status)
                              }
                            >
                              {status}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tp-empty">
              <h3>No active students</h3>
              <p>This class has no active students to mark.</p>
            </div>
          )}
        </>
      ) : (
        <div className="tp-empty">
          <CalendarIcon />
          <h3>Select a class</h3>
          <p>
            Choose class, subject, and period, or open a timetable slot directly.
          </p>
        </div>
      )}
    </div>
  );
}

export default TeacherAttendance;
